import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MultiSelect } from "@/components/MultiSelect";
import { STATUS } from "@/lib/api";
import { ChevronDown, ChevronUp, Filter, X } from "lucide-react";

export const MULTI_KEYS = ["status", "entregador_id", "bairro_id", "rota_id", "tipo_id", "forma_pgto_id", "regiao_id"];
const TEXT_KEYS = ["numero_pedido", "cliente_nome", "cliente_telefone", "valor_min", "valor_max"];
export const EMPTY_FILTERS = { start: "", end: "", status: [], entregador_id: [], bairro_id: [], rota_id: [], tipo_id: [], forma_pgto_id: [], regiao_id: [], numero_pedido: "", cliente_nome: "", cliente_telefone: "", valor_min: "", valor_max: "" };
const STATUS_OPTS = Object.entries(STATUS).map(([id, nome]) => ({ id, nome }));
const fmtD = (s) => (s ? s.split("-").reverse().join("/") : "");

/** Chips-resumo dos filtros ativos (mostrados com o card recolhido). */
export function activeFilterChips(filters, cat) {
  const chips = [];
  if (filters.start || filters.end) chips.push({ k: "periodo", label: filters.start && filters.end && filters.start === filters.end ? fmtD(filters.start) : `${fmtD(filters.start) || "…"} → ${fmtD(filters.end) || "…"}` });
  const nameOf = (arr, id, extras = {}) => extras[id] || arr.find((x) => x.id === id)?.nome || id;
  const lbl = {
    status: (v) => STATUS[v] || v, entregador_id: (v) => nameOf(cat.entregadores, v, { __none__: "Sem entregador" }),
    bairro_id: (v) => nameOf(cat.bairros, v), rota_id: (v) => nameOf(cat.rotas, v, { __none__: "Sem rota" }),
    tipo_id: (v) => nameOf(cat.tipos, v), forma_pgto_id: (v) => nameOf(cat.formas, v), regiao_id: (v) => nameOf(cat.regioes, v),
  };
  const title = { status: "Status", entregador_id: "Entregador", bairro_id: "Bairro", rota_id: "Rota", tipo_id: "Tipo", forma_pgto_id: "Forma", regiao_id: "Região" };
  for (const k of MULTI_KEYS) {
    const arr = filters[k] || [];
    if (arr.length === 1) chips.push({ k, label: `${title[k]}: ${lbl[k](arr[0])}` });
    else if (arr.length > 1) chips.push({ k, label: `${title[k]}: ${arr.length} selecionados` });
  }
  const tt = { numero_pedido: "Pedido", cliente_nome: "Cliente", cliente_telefone: "Tel", valor_min: "Mín", valor_max: "Máx" };
  for (const k of TEXT_KEYS) if (filters[k] !== "" && filters[k] != null) chips.push({ k, label: `${tt[k]}: ${filters[k]}` });
  return chips;
}

/**
 * Cartão de filtros (topo da tela de Entregas).
 * Iter 116: todos os filtros de catálogo aceitam seleção múltipla (MultiSelect) + card recolhível
 * com chips dos filtros ativos, pra tabela ganhar altura.
 */
export function EntregasFilters({ filters, setFilters, entregadores, bairros, rotas = [], tipos = [], formas = [], regioes = [], programadasCount, setQuickFilter }) {
  const [open, setOpen] = useState(true);
  const cat = { entregadores, bairros, rotas, tipos, formas, regioes };
  const chips = activeFilterChips(filters, cat);
  const setMulti = (k) => (arr) => setFilters({ ...filters, [k]: arr });
  const clearChip = (k) => setFilters({ ...filters, ...(k === "periodo" ? { start: "", end: "" } : { [k]: MULTI_KEYS.includes(k) ? [] : "" }) });
  return (
    <Card data-testid="entregas-filters-card" data-open={open}>
      <CardHeader className="py-2.5 px-4 flex flex-row justify-between items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left" data-testid="btn-toggle-filters" aria-expanded={open}>
          <Filter className="w-4 h-4 text-orange-600" />
          <CardTitle className="text-base">Filtros</CardTitle>
          {chips.length > 0 && <span className="rounded-full bg-orange-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid="filters-active-count">{chips.length}</span>}
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        <div className="flex gap-1 items-center flex-wrap">
          {!open && chips.map((c) => (
            <span key={c.k} className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 text-orange-900 text-[11px] px-2 py-0.5" data-testid={`filter-chip-${c.k}`}>
              {c.label}
              <button type="button" onClick={() => clearChip(c.k)} className="hover:text-rose-600" title="Remover filtro" data-testid={`filter-chip-${c.k}-remove`}><X className="w-3 h-3" /></button>
            </span>
          ))}
          <span className="text-xs text-muted-foreground ml-1 mr-1">Rápido:</span>
          <Button size="sm" variant="outline" onClick={() => setQuickFilter("hoje")} data-testid="btn-filter-hoje" className="h-7 text-xs">Hoje</Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFilter("amanha")} data-testid="btn-filter-amanha" className="h-7 text-xs">Amanhã</Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFilter("programadas")} data-testid="btn-filter-programadas" className="h-7 text-xs">
            Programadas{programadasCount > 0 && <span className="ml-1 bg-orange-600 text-white rounded-full px-1.5 text-[10px]">{programadasCount}</span>}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFilter("todas")} data-testid="btn-filter-todas" className="h-7 text-xs">Todas</Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2.5 pt-0 pb-3 px-4" data-testid="entregas-filters-body">
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
              onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { console.debug("showPicker indisponível", err); } }} data-testid="filter-start" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
              onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { console.debug("showPicker indisponível", err); } }} data-testid="filter-end" />
          </div>
          <div><Label className="text-xs">Status</Label>
            <MultiSelect options={STATUS_OPTS} value={filters.status} onChange={setMulti("status")} placeholder="Todos" testId="filter-status" searchable={false} /></div>
          <div><Label className="text-xs">Entregador</Label>
            <MultiSelect options={entregadores} extras={[{ value: "__none__", label: "Sem entregador" }]} value={filters.entregador_id} onChange={setMulti("entregador_id")} placeholder="Todos" testId="filter-entregador" /></div>
          <div><Label className="text-xs">Bairro</Label>
            <MultiSelect options={bairros} value={filters.bairro_id} onChange={setMulti("bairro_id")} placeholder="Todos" testId="filter-bairro" /></div>
          <div><Label className="text-xs">Rota</Label>
            <MultiSelect options={rotas.map((r) => ({ id: r.id, nome: r.nome + (r.ativa === false ? " (inativa)" : ""), cor: r.cor }))} extras={[{ value: "__none__", label: "Sem rota" }]}
              value={filters.rota_id} onChange={setMulti("rota_id")} placeholder="Todas" testId="filter-rota" /></div>
          <div><Label className="text-xs">Tipo</Label>
            <MultiSelect options={tipos} value={filters.tipo_id} onChange={setMulti("tipo_id")} placeholder="Todos" testId="filter-tipo" /></div>
          <div><Label className="text-xs">Forma Pgto</Label>
            <MultiSelect options={formas} value={filters.forma_pgto_id} onChange={setMulti("forma_pgto_id")} placeholder="Todas" testId="filter-forma" /></div>
          <div><Label className="text-xs">Região</Label>
            <MultiSelect options={regioes} value={filters.regiao_id} onChange={setMulti("regiao_id")} placeholder="Todas" testId="filter-regiao" /></div>
          <div>
            <Label className="text-xs">Cliente</Label>
            <Input value={filters.cliente_nome} onChange={(e) => setFilters({ ...filters, cliente_nome: e.target.value })} placeholder="Nome" data-testid="filter-cliente-nome" />
          </div>
          <div>
            <Label className="text-xs">Telefone</Label>
            <Input value={filters.cliente_telefone} onChange={(e) => setFilters({ ...filters, cliente_telefone: e.target.value })} placeholder="(DDD) número" inputMode="numeric" data-testid="filter-cliente-tel" />
          </div>
          <div>
            <Label className="text-xs">Nº Pedido</Label>
            <Input value={filters.numero_pedido} onChange={(e) => setFilters({ ...filters, numero_pedido: e.target.value })} placeholder="Buscar" data-testid="filter-pedido" />
          </div>
          <div>
            <Label className="text-xs">Valor mín</Label>
            <Input type="number" step="0.01" value={filters.valor_min} onChange={(e) => setFilters({ ...filters, valor_min: e.target.value })} placeholder="R$" data-testid="filter-valor-min" />
          </div>
          <div>
            <Label className="text-xs">Valor máx</Label>
            <Input type="number" step="0.01" value={filters.valor_max} onChange={(e) => setFilters({ ...filters, valor_max: e.target.value })} placeholder="R$" data-testid="filter-valor-max" />
          </div>
          <div className="col-span-2 md:col-span-4 xl:col-span-7 flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Padrão: só o dia atual (De=Até=hoje). Nos filtros de lista, marque quantos valores quiser.</span>
            <Button size="sm" variant="ghost" onClick={() => setFilters({ ...EMPTY_FILTERS })} data-testid="btn-clear-filters">Limpar filtros</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
